import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Add from '@mui/icons-material/Add';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import Inventory2 from '@mui/icons-material/Inventory2';
import Task from '@mui/icons-material/Task';
import Computer from '@mui/icons-material/Computer';

const QUICK_CREATE_ITEMS = [
  { label: 'New Ticket', icon: <ConfirmationNumber fontSize="small" />, path: '/tickets' },
  { label: 'New Asset', icon: <Inventory2 fontSize="small" />, path: '/assets' },
  { label: 'New Hardware Request', icon: <Computer fontSize="small" />, path: '/hardware/requests' },
  { label: 'New Maintenance', icon: <Task fontSize="small" />, path: '/maintenance-mgmt/new' },
];

/**
 * QuickCreate — dropdown menu for quickly creating common entities.
 *
 * Features:
 * - Renders as an IconButton with Add icon
 * - Dropdown with 4 items: Ticket, Asset, Task, Hardware Request
 * - Selection closes the dropdown and navigates to the creation form
 * - Keyboard shortcut: Ctrl+N / Cmd+N toggles the dropdown
 * - Closes on outside click or Escape
 * - Tooltip shows "New (Ctrl+N)" shortcut hint
 */
export default function QuickCreate() {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const buttonRef = useRef(null);

  const handleOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleItemClick = useCallback((path) => {
    setAnchorEl(null);
    navigate(path);
  }, [navigate]);

  // Register Ctrl+N / Cmd+N keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (event) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? event.metaKey : event.ctrlKey;

      if (modifier && event.key === 'n') {
        event.preventDefault();
        setAnchorEl((prev) => (prev ? null : buttonRef.current));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      <Tooltip title="New (Ctrl+N)" arrow>
        <IconButton
          ref={buttonRef}
          onClick={handleOpen}
          size="small"
          aria-label="Quick create"
          aria-haspopup="true"
          aria-expanded={open}
          color="inherit"
        >
          <Add />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { minWidth: 200, mt: 0.5 },
          },
        }}
      >
        {QUICK_CREATE_ITEMS.map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => handleItemClick(item.path)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
